import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const CaseDetailPanel = ({ 
  caseData, 
  onClose, 
  onEdit, 
  onScheduleHearing, 
  onCreateInvoice,
  language = 'en',
  direction = 'ltr' 
}) => {
  const [activeTab, setActiveTab] = useState('overview');

  const labels = {
    caseDetails: {
      en: 'Case Details',
      fr: 'Détails du dossier',
      ar: 'تفاصيل القضية'
    },
    overview: {
      en: 'Overview',
      fr: 'Aperçu',
      ar: 'نظرة عامة'
    },
    timeline: {
      en: 'Timeline',
      fr: 'Chronologie',
      ar: 'الجدول الزمني'
    },
    documents: {
      en: 'Documents',
      fr: 'Documents',
      ar: 'المستندات'
    },
    billing: {
      en: 'Billing',
      fr: 'Facturation',
      ar: 'الفواتير'
    },
    edit: {
      en: 'Edit Case',
      fr: 'Modifier le dossier',
      ar: 'تعديل القضية'
    },
    scheduleHearing: {
      en: 'Schedule Hearing',
      fr: 'Planifier une audience',
      ar: 'جدولة جلسة'
    },
    createInvoice: {
      en: 'Create Invoice',
      fr: 'Créer une facture',
      ar: 'إنشاء فاتورة'
    },
    caseNumber: {
      en: 'Case Number',
      fr: 'Numéro de dossier',
      ar: 'رقم القضية'
    },
    client: {
      en: 'Client',
      fr: 'Client',
      ar: 'العميل'
    },
    practiceArea: {
      en: 'Practice Area',
      fr: 'Domaine de pratique',
      ar: 'مجال الممارسة'
    },
    assignedLawyer: {
      en: 'Assigned Lawyer',
      fr: 'Avocat assigné',
      ar: 'المحامي المعين'
    },
    status: {
      en: 'Status',
      fr: 'Statut',
      ar: 'الحالة'
    },
    openedDate: {
      en: 'Opened Date',
      fr: 'Date d\'ouverture',
      ar: 'تاريخ الفتح'
    },
    description: {
      en: 'Case Description',
      fr: 'Description du dossier',
      ar: 'وصف القضية'
    },
    legalNotes: {
      en: 'Legal Notes',
      fr: 'Notes juridiques',
      ar: 'ملاحظات قانونية'
    },
    nextHearing: {
      en: 'Next Hearing',
      fr: 'Prochaine audience',
      ar: 'الجلسة القادمة'
    },
    courtName: {
      en: 'Court Name',
      fr: 'Nom du tribunal',
      ar: 'اسم المحكمة'
    },
    totalBilled: {
      en: 'Total Billed',
      fr: 'Total facturé',
      ar: 'المبلغ الإجمالي'
    },
    totalPaid: {
      en: 'Total Paid',
      fr: 'Total payé',
      ar: 'المبلغ المدفوع'
    },
    outstanding: {
      en: 'Outstanding',
      fr: 'En attente',
      ar: 'المتبقي'
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      intake: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      investigation: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      litigation: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      negotiation: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      appeal: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
      closed: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
    };
    return colors?.[status] || colors?.intake;
  };

  const getStatusLabel = (status) => {
    const statusLabels = {
      intake: language === 'ar' ? 'استقبال' : language === 'fr' ? 'Réception' : 'Intake',
      investigation: language === 'ar' ? 'تحقيق' : language === 'fr' ? 'Enquête' : 'Investigation',
      litigation: language === 'ar' ? 'تقاضي' : language === 'fr' ? 'Contentieux' : 'Litigation',
      negotiation: language === 'ar' ? 'تفاوض' : language === 'fr' ? 'Négociation' : 'Negotiation',
      appeal: language === 'ar' ? 'استئناف' : language === 'fr' ? 'Appel' : 'Appeal',
      closed: language === 'ar' ? 'مغلق' : language === 'fr' ? 'Clôturé' : 'Closed'
    };
    return statusLabels?.[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (language === 'ar') {
      return date?.toLocaleDateString('ar-MA');
    }
    return date?.toLocaleDateString('fr-MA');
  };

  const formatCurrency = (amount) => {
    if (language === 'ar') {
      return `${amount?.toLocaleString('ar-MA')} د.م.`;
    }
    return `${amount?.toLocaleString('fr-MA')} MAD`;
  };

  const tabs = [
    { id: 'overview', label: labels?.overview?.[language], icon: 'FileText' },
    { id: 'timeline', label: labels?.timeline?.[language], icon: 'Clock' },
    { id: 'documents', label: labels?.documents?.[language], icon: 'FolderOpen' },
    { id: 'billing', label: labels?.billing?.[language], icon: 'Receipt' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden" style={{ direction }}>
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-secondary p-4 md:p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h2 className="text-lg md:text-xl font-semibold text-white mb-2">
              {labels?.caseDetails?.[language]}
            </h2>
            <p className="text-sm text-white/90 monospace">{caseData?.caseNumber}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClose}
            className="text-white hover:bg-white/20"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            size="sm"
            iconName="Edit"
            iconPosition="left"
            onClick={onEdit}
          >
            {labels?.edit?.[language]}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            iconName="Calendar"
            iconPosition="left"
            onClick={onScheduleHearing}
          >
            {labels?.scheduleHearing?.[language]}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            iconName="FileText"
            iconPosition="left"
            onClick={onCreateInvoice}
          >
            {labels?.createInvoice?.[language]}
          </Button>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-border overflow-x-auto">
        <div className="flex min-w-max">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              className={`flex items-center gap-2 px-4 md:px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setActiveTab(tab?.id)}
            >
              <Icon name={tab?.icon} size={18} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Content */}
      <div className="p-4 md:p-6 max-h-[600px] overflow-y-auto">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground font-medium mb-1 block">
                  {labels?.client?.[language]}
                </label>
                <p className="text-sm text-foreground font-medium">{caseData?.clientName}</p>
              </div>

              <div>
                <label className="text-xs text-muted-foreground font-medium mb-1 block">
                  {labels?.status?.[language]}
                </label>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(caseData?.status)}`}>
                  {getStatusLabel(caseData?.status)}
                </span>
              </div>

              <div>
                <label className="text-xs text-muted-foreground font-medium mb-1 block">
                  {labels?.practiceArea?.[language]}
                </label>
                <p className="text-sm text-foreground">{caseData?.practiceArea}</p>
              </div>

              <div>
                <label className="text-xs text-muted-foreground font-medium mb-1 block">
                  {labels?.assignedLawyer?.[language]}
                </label>
                <p className="text-sm text-foreground">{caseData?.assignedLawyer}</p>
              </div>

              <div>
                <label className="text-xs text-muted-foreground font-medium mb-1 block">
                  {labels?.openedDate?.[language]}
                </label>
                <p className="text-sm text-foreground">{formatDate(caseData?.openedDate)}</p>
              </div>

              <div>
                <label className="text-xs text-muted-foreground font-medium mb-1 block">
                  {labels?.nextHearing?.[language]}
                </label>
                <p className="text-sm text-foreground">{formatDate(caseData?.nextHearing)}</p>
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground font-medium mb-2 block">
                {labels?.description?.[language]}
              </label>
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">
                {caseData?.description}
              </p>
            </div>

            <div>
              <label className="text-xs text-muted-foreground font-medium mb-2 block">
                {labels?.legalNotes?.[language]}
              </label>
              <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">
                {caseData?.legalNotes}
              </p>
            </div>

            {caseData?.courtName && (
              <div>
                <label className="text-xs text-muted-foreground font-medium mb-1 block">
                  {labels?.courtName?.[language]}
                </label>
                <p className="text-sm text-foreground">{caseData?.courtName}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'timeline' && (
          <div className="space-y-4">
            {caseData?.timeline?.map((event, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    event?.type === 'hearing' ? 'bg-primary/10 text-primary' :
                    event?.type === 'document' ? 'bg-accent/10 text-accent' :
                    event?.type === 'status'? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                  }`}>
                    <Icon name={event?.icon} size={18} />
                  </div>
                  {index < caseData?.timeline?.length - 1 && (
                    <div className="w-0.5 h-full bg-border mt-2" />
                  )}
                </div>
                <div className="flex-1 pb-6">
                  <p className="text-sm font-medium text-foreground mb-1">{event?.title}</p>
                  <p className="text-sm text-muted-foreground mb-2">{event?.description}</p>
                  <p className="text-xs text-muted-foreground">{formatDate(event?.date)}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="space-y-3">
            {caseData?.documents?.map((doc) => (
              <div
                key={doc?.id}
                className="flex items-center gap-3 p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="FileText" size={20} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{doc?.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {doc?.size} • {formatDate(doc?.uploadDate)}
                  </p>
                </div>
                <Button variant="ghost" size="sm" iconName="Download" />
              </div>
            ))}
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-muted rounded-lg p-4">
                <label className="text-xs text-muted-foreground font-medium mb-2 block">
                  {labels?.totalBilled?.[language]}
                </label>
                <p className="text-xl md:text-2xl font-semibold text-foreground monospace">
                  {formatCurrency(caseData?.billing?.totalBilled)}
                </p>
              </div>

              <div className="bg-success/10 rounded-lg p-4">
                <label className="text-xs text-success font-medium mb-2 block">
                  {labels?.totalPaid?.[language]}
                </label>
                <p className="text-xl md:text-2xl font-semibold text-success monospace">
                  {formatCurrency(caseData?.billing?.totalPaid)}
                </p>
              </div>

              <div className="bg-warning/10 rounded-lg p-4">
                <label className="text-xs text-warning font-medium mb-2 block">
                  {labels?.outstanding?.[language]}
                </label>
                <p className="text-xl md:text-2xl font-semibold text-warning monospace">
                  {formatCurrency(caseData?.billing?.outstanding)}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {caseData?.billing?.invoices?.map((invoice) => (
                <div
                  key={invoice?.id}
                  className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium text-foreground monospace mb-1">
                      {invoice?.invoiceNumber}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(invoice?.date)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-foreground monospace mb-1">
                      {formatCurrency(invoice?.amount)}
                    </p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      invoice?.status === 'paid' ? 'bg-success/10 text-success' :
                      invoice?.status === 'pending'? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
                    }`}>
                      {invoice?.status === 'paid' 
                        ? (language === 'ar' ? 'مدفوع' : language === 'fr' ? 'Payé' : 'Paid')
                        : invoice?.status === 'pending'
                        ? (language === 'ar' ? 'قيد الانتظار' : language === 'fr' ? 'En attente' : 'Pending')
                        : (language === 'ar' ? 'متأخر' : language === 'fr' ? 'En retard' : 'Overdue')
                      }
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseDetailPanel;